import * as React from 'react';

import { useLockFn, useRequest, useUpdateEffect } from 'ahooks';
import {
  Badge,
  Button,
  Dropdown,
  Flex,
  Image,
  Modal,
  notification,
  Select,
  Space,
} from 'antd';

import {
  AntdRouterExternalLink,
  AntdRouterLink,
  CheckCircleOutlinedIcon,
  CloseCircleOutlinedIcon,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  EyeOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import { deleteProduct, getBrands, getCategories } from 'services';
import type { ProductStatus, ProductSummary } from 'types';
import {
  adjustPageNumber,
  dateTimeFormat,
  FILE,
  generateDownloadFile,
  generateExternalPathname,
  generateSelectOptions,
  generateTableDataSource,
  localizeProductStatus,
  normalizeToString,
  numberFormat,
  PATHNAME,
  UNIT,
} from 'utils';

const { PRICE } = UNIT;
const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    PRODUCTS: { UPDATE_URL: PRODUCTS_UPDATE_URL },
    BRANDS: { BASE_URL: BRANDS_BASE_URL, UPDATE_URL: BRANDS_UPDATE_URL },
    CATEGORIES: {
      BASE_URL: CATEGORIES_BASE_URL,
      UPDATE_URL: CATEGORIES_UPDATE_URL,
    },
    PRODUCT: { BASE_URL: PRODUCT_BASE_URL },
  },
} = PATHNAME;
const {
  IMAGE: {
    SIZE: { WIDTH: IMAGE_SIZE_WIDTH, HEIGHT: IMAGE_SIZE_HEIGHT },
  },
} = FILE;

const productStatuses = [
  'Submitted',
  'Draft',
  'Private',
] satisfies ProductStatus[];

type ProductsTableProps = {
  loading: boolean;
  total: number;
  products: ProductSummary[];
  pageSize: number;
  pageNumber: number;
  handleCategoriesIds: (categoriesIds: string[]) => void;
  handleBrandsIds: (brandsIds: string[]) => void;
  handleStatuses: (statuses: ProductStatus[]) => void;
  handleCallForPrice: (callForPrice: boolean) => void;
  handleIndex: (isIndex: boolean) => void;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
  handleRefreshData: () => void;
};

const ProductsTable = React.memo<ProductsTableProps>(
  ({
    loading,
    total,
    products,
    pageSize,
    pageNumber,
    handleCategoriesIds,
    handleBrandsIds,
    handleStatuses,
    handleCallForPrice,
    handleIndex,
    handlePageSize,
    handlePageNumber,
    handleRefreshData,
  }: ProductsTableProps) => {
    const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();
    const { data: categoriesData, loading: categoriesLoading } = useRequest(
      getCategories,
      {
        cacheKey: 'categories',
      },
    );
    const { data: brandsData, loading: brandsLoading } = useRequest(getBrands, {
      cacheKey: 'brands',
    });

    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const handleDeleteProduct = useLockFn(async ({ id }: ProductSummary) => {
      deleteProduct({ id })
        .then(() => {
          handleRefreshData();
          notification.success({
            message: 'محصول مورد نظر با موفقیت حذف شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        });
    });

    const { categories = [] } = categoriesData ?? {};
    const { brands = [] } = brandsData ?? {};

    const productsDataSource = generateTableDataSource(products, 'id');
    const categoriesSelectOptions = generateSelectOptions(
      categories,
      'id',
      'title',
      'id',
    );
    const brandsSelectOptions = generateSelectOptions(
      brands,
      'id',
      'title',
      'id',
    );

    return (
      <PrimitiveTable
        scroll={{ x: 1750, y: 630 }}
        columns={[
          {
            title: 'نام',
            dataIndex: 'name',
            key: 'name',
            width: '25%',
            ellipsis: true,
            render: (_, { id, name, bannerId }) => (
              <Space>
                <Image
                  src={generateDownloadFile(
                    'Product',
                    bannerId,
                    IMAGE_SIZE_WIDTH,
                    IMAGE_SIZE_HEIGHT,
                  )}
                  width={40}
                  height={40}
                  alt={name}
                />
                <AntdRouterLink
                  to={`${PRODUCTS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                  className='text-wrap'
                >
                  {name}
                </AntdRouterLink>
              </Space>
            ),
          },
          {
            title: 'دسته‌بندی',
            dataIndex: 'mainCategory',
            key: 'mainCategory',
            width: 140,
            ellipsis: true,
            filterDropdown: () => (
              <div className='bg-[--ant-table-header-bg] p-2'>
                <Select<string[]>
                  showSearch
                  mode='multiple'
                  maxTagCount='responsive'
                  className='w-72'
                  placeholder='دسته‌بندی مورد نظر را انتخاب کنید'
                  loading={categoriesLoading}
                  options={categoriesSelectOptions}
                  filterOption={(inputValue, option) =>
                    normalizeToString(option?.label)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  onChange={value => {
                    handleCategoriesIds(value);
                  }}
                />
              </div>
            ),
            render: (_, { mainCategory }) => {
              if (!mainCategory) return null;

              const { id, title } = mainCategory;

              return (
                <AntdRouterExternalLink
                  ellipsis
                  href={`${CATEGORIES_BASE_URL}${CATEGORIES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                >
                  {title}
                </AntdRouterExternalLink>
              );
            },
          },
          {
            title: 'برند',
            dataIndex: 'brand',
            key: 'brand',
            width: 110,
            ellipsis: true,
            filterDropdown: () => (
              <div className='bg-[--ant-table-header-bg] p-2'>
                <Select<string[]>
                  showSearch
                  mode='multiple'
                  maxTagCount='responsive'
                  className='w-72'
                  placeholder='برند مورد نظر را انتخاب کنید'
                  loading={brandsLoading}
                  options={brandsSelectOptions}
                  filterOption={(inputValue, option) =>
                    normalizeToString(option?.label)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  onChange={value => {
                    handleBrandsIds(value);
                  }}
                />
              </div>
            ),
            render: (_, { brand }) => {
              if (!brand) return null;

              const { id, title } = brand;

              return (
                <AntdRouterExternalLink
                  ellipsis
                  href={`${BRANDS_BASE_URL}${BRANDS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                >
                  {title}
                </AntdRouterExternalLink>
              );
            },
          },
          {
            title: 'قیمت',
            dataIndex: 'mainSubProduct',
            key: 'mainSubProduct',
            ellipsis: true,
            render: (_, { mainSubProduct }) => {
              const { originalPrice, price } = mainSubProduct ?? {};
              return (
                <div>
                  {numberFormat(price ?? originalPrice, undefined, ` ${PRICE}`)}
                </div>
              );
            },
          },
          {
            title: 'کل موجودی',
            dataIndex: 'totalStock',
            key: 'totalStock',
            width: 110,
            ellipsis: true,
            render: (_, { totalStock }) => (
              <div className='text-center'>{numberFormat(totalStock)}</div>
            ),
          },
          {
            title: 'وضعیت',
            dataIndex: 'status',
            key: 'status',
            width: 115,
            ellipsis: true,
            filterDropdown: () => (
              <div className='bg-[--ant-table-header-bg] p-2'>
                <Select<ProductStatus[]>
                  showSearch
                  mode='multiple'
                  maxTagCount='responsive'
                  className='w-72'
                  placeholder='وضعیت مورد نظر را انتخاب کنید'
                  options={productStatuses.map(type => {
                    const { label } = localizeProductStatus(type);
                    return {
                      label,
                      value: type,
                    };
                  })}
                  filterOption={(inputValue, option) =>
                    normalizeToString(option?.label)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  onChange={value => {
                    handleStatuses(value);
                  }}
                />
              </div>
            ),
            render: (_, { status }) => {
              const { label, color } = localizeProductStatus(status);
              return (
                <Flex justify='center'>
                  <Badge count={label} color={color} />
                </Flex>
              );
            },
          },
          {
            title: 'استعلام بها',
            dataIndex: 'callForPrice',
            key: 'callForPrice',
            width: 120,
            ellipsis: true,
            filterMultiple: false,
            filterDropdown: () => (
              <div className='bg-[--ant-table-header-bg] p-2'>
                <Select<boolean>
                  allowClear
                  className='w-72'
                  placeholder='استعلام مورد نظر را انتخاب کنید'
                  options={[
                    {
                      label: 'استعلام بها دارد',
                      value: 'true',
                    },
                    {
                      label: 'استعلام بها ندارد',
                      value: 'false',
                    },
                  ]}
                  onChange={value => {
                    handleCallForPrice(value);
                  }}
                />
              </div>
            ),
            render: (_, { callForPrice }) => (
              <Flex justify='center'>
                {callForPrice ? (
                  <CheckCircleOutlinedIcon className='text-green-500' />
                ) : (
                  <CloseCircleOutlinedIcon className='text-red-500' />
                )}
              </Flex>
            ),
          },
          {
            title: 'نمایه‌گذاری',
            dataIndex: 'seoIndex',
            key: 'seoIndex',
            width: 120,
            ellipsis: true,
            filterMultiple: false,
            filterDropdown: () => (
              <div className='bg-[--ant-table-header-bg] p-2'>
                <Select<boolean>
                  allowClear
                  className='w-72'
                  placeholder='نمایه‌گذاری مورد نظر را انتخاب کنید'
                  options={[
                    {
                      label: 'نمایه‌گذاری دارد',
                      value: 'true',
                    },
                    {
                      label: 'نمایه‌گذاری ندارد',
                      value: 'false',
                    },
                  ]}
                  onChange={value => {
                    handleIndex(value);
                  }}
                />
              </div>
            ),
            render: (_, { seo }) => {
              const { isIndex } = seo ?? {};
              return (
                <Flex justify='center'>
                  {isIndex ? (
                    <CheckCircleOutlinedIcon className='text-green-500' />
                  ) : (
                    <CloseCircleOutlinedIcon className='text-red-500' />
                  )}
                </Flex>
              );
            },
          },
          {
            title: 'تاریخ ساخت',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 170,
            ellipsis: true,
            render: (_, { createdDate }) => (
              <bdi>{dateTimeFormat(new Date(createdDate))}</bdi>
            ),
          },
          {
            title: 'تاریخ آخرین ویرایش',
            dataIndex: 'lastModifiedDate',
            key: 'lastModifiedDate',
            width: 170,
            ellipsis: true,
            render: (_, { lastModifiedDate }) => (
              <bdi>{dateTimeFormat(new Date(lastModifiedDate))}</bdi>
            ),
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id, slug } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'see-product',
                          label: (
                            <AntdRouterExternalLink
                              href={generateExternalPathname(
                                PRODUCT_BASE_URL,
                                slug,
                              )}
                              className='inline-block py-1 text-inherit'
                            >
                              <EyeOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                مشاهده در فروشگاه
                              </span>
                            </AntdRouterExternalLink>
                          ),
                        },
                        {
                          key: 'update-product',
                          label: (
                            <AntdRouterLink
                              to={`${PRODUCTS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>ویرایش محصول</span>
                            </AntdRouterLink>
                          ),
                        },
                        {
                          key: 'delete-product',
                          label: (
                            <React.Fragment>
                              <Button
                                danger
                                type='link'
                                size='small'
                                className='my-0.5 flex items-center px-0'
                                onClick={async () => {
                                  const confirmed =
                                    await deleteModalInstance.confirm({
                                      title:
                                        'آیا از حذف محصول مورد نظر اطمینان دارید؟',
                                      width: 500,
                                    });
                                  if (confirmed) {
                                    handleDeleteProduct(record);
                                  }
                                }}
                              >
                                <DeleteOutlinedIcon />
                                <span className='text-sm'>حذف محصول</span>
                              </Button>
                              {deleteModalContextHolder}
                            </React.Fragment>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={productsDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
ProductsTable.displayName = 'ProductsTable';

export { ProductsTable };
